/**
 * Detail drawer for one evaluation criterion (Analysis tab).
 *
 * Deliberately the same shape as `RiskDrawer` / `RequirementDrawer`: a plain
 * <aside> beside the table on desktop (>=lg) so the rows stay clickable, and
 * `ui/sheet.tsx` below that, where the two-column layout does not exist. Head /
 * scrolling body / pinned composer footer, and the same `w-28` label column.
 *
 * Unlike the risk drawer this one is purely a reading surface plus a thread. A
 * criterion carries no owner and no status of its own, and correcting an
 * extracted fact in place is a separate job, so nothing here is editable. What
 * it adds over the table row is the full description text (the table clamps it
 * to two lines) and the discussion.
 */

import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { CitationLink } from '@/components/extraction/CitationLink'
import { RequirementDiscussion } from '@/components/extraction/RequirementDiscussion'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import { normalizeTitle } from '@/lib/criterion-title'
import { cn } from '@/lib/utils'
import type { ExtractedCriterion } from '@/types/extraction'

// ===================
// Types
// ===================

export interface CriterionDrawerProps {
  bidId: string
  /** `null` renders nothing — the host keeps the drawer mounted and passes the active row. */
  item: ExtractedCriterion | null
  /** The `1.2`-style outline number from the host's flattened rows, for the head. */
  itemNumber: string | null
  onClose: () => void
  readOnly: boolean
}

// ===================
// Helpers
// ===================

/**
 * Percentage and points are independent: a tender may state either, both, or
 * neither. `null` is "not stated" — a literal `0%` is a stated weight and is
 * shown as one, which is why this tests for `null` rather than falsiness.
 */
function weightParts(criterion: ExtractedCriterion): string[] {
  const parts: string[] = []
  if (criterion.weightPercentage != null) parts.push(`${criterion.weightPercentage}%`)
  if (criterion.weightPoints != null) parts.push(`${criterion.weightPoints} pts`)
  return parts
}

// ===================
// Pieces
// ===================

function LabelledRow({
  label,
  align = 'center',
  children,
}: {
  label: string
  align?: 'center' | 'start'
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex gap-3', align === 'center' ? 'items-center' : 'items-start')}>
      <span className={cn('w-28 shrink-0 text-meta', align === 'start' && 'pt-0.5')}>{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

// ===================
// Body
// ===================

interface DrawerBodyProps {
  bidId: string
  item: ExtractedCriterion
  itemNumber: string | null
  onClose: () => void
  readOnly: boolean
  /** The sheet needs a Radix <SheetTitle> for its dialog label; the aside uses a plain <h2>. */
  titleAs?: 'h2' | 'sheet'
}

function DrawerBody({
  bidId,
  item,
  itemNumber,
  onClose,
  readOnly,
  titleAs = 'h2',
}: DrawerBodyProps) {
  /**
   * The pinned footer the discussion portals its composer into. Held as state
   * rather than a ref so the child re-renders once the element exists.
   */
  const [composerSlot, setComposerSlot] = useState<HTMLDivElement | null>(null)

  const itemId = item.id
  const parts = weightParts(item)

  const titleClassName = 'text-sm font-medium'
  const heading = normalizeTitle(item.title)
  const title =
    titleAs === 'sheet' ? (
      <SheetTitle className={titleClassName}>{heading}</SheetTitle>
    ) : (
      <h2 className={titleClassName}>{heading}</h2>
    )

  return (
    <>
      {/* Head — fixed */}
      <div className="flex items-start justify-between gap-2 border-b border-border p-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-start gap-1.5">
            {itemNumber && <span className="shrink-0 pt-px text-meta">{itemNumber}</span>}
            {title}
          </div>
          <p className="text-meta">{parts.length ? parts.join(' / ') : 'No weight stated'}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Close details"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body — scrolls */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="space-y-2.5">
          {/* The table clamps the description to two lines; this is where the
              whole of it finally lives.

              Full width, NOT behind the w-28 label column: that column exists to
              line up CONTROLS, and this drawer has none. Indenting a paragraph
              behind it cut the measure to ~250px of a 400px panel and broke the
              lines early. This now matches RequirementDrawer's shape exactly —
              title, prose, source, thread. */}
          {item.description && (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {item.description}
            </p>
          )}

          {/* Only when a criterion carries both figures: the head already
              states a lone weight, and repeating it verbatim would add a row
              that says nothing new. */}
          {parts.length > 1 && (
            <LabelledRow label="Weight">
              <div className="flex items-baseline gap-3 text-sm">
                {parts.map((part) => (
                  <span key={part}>{part}</span>
                ))}
              </div>
            </LabelledRow>
          )}
        </div>

        {/* Source */}
        {item.sourceQuote && (
          <div className="space-y-1.5 border-t border-border pt-3">
            <span className="text-sm font-medium text-foreground">Source</span>
            <blockquote className="border-l-2 border-border pl-2.5 text-xs text-muted-foreground">
              &ldquo;{item.sourceQuote}&rdquo;
            </blockquote>
            <CitationLink
              page={item.sourcePage}
              documentId={item.sourceDocumentId ?? null}
              documentName={item.sourceDocument}
              sourceQuote={item.sourceQuote}
              sourceHighlightData={item.sourceHighlightData ?? null}
            />
          </div>
        )}

        {/* Discussion — a manual row that has never been saved has no id to
            hang a thread off yet. */}
        {itemId && (
          <div className="mt-2 pt-1">
            <RequirementDiscussion
              bidId={bidId}
              itemId={itemId}
              targetType="criterion"
              readOnly={readOnly}
              composerSlot={composerSlot}
            />
          </div>
        )}
      </div>

      {/* Pinned composer footer — filled by RequirementDiscussion's portal, and
          collapsed by `empty:hidden` when the viewer may not post. */}
      {itemId && (
        <div ref={setComposerSlot} className="shrink-0 border-t border-border p-3 empty:hidden" />
      )}
    </>
  )
}

// ===================
// Component
// ===================

export function CriterionDrawer({
  bidId,
  item,
  itemNumber,
  onClose,
  readOnly,
}: CriterionDrawerProps) {
  const isDesktop = useIsDesktop()

  if (!item) return null

  const shared = { bidId, item, itemNumber, onClose, readOnly }

  if (!isDesktop) {
    return (
      <Sheet open onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" hideClose className="flex w-full flex-col p-0 sm:max-w-md">
          <DrawerBody {...shared} titleAs="sheet" />
        </SheetContent>
      </Sheet>
    )
  }

  // Width grows with the screen instead of sitting at a fixed 400px.
  // Floor 400px: at a 1200px viewport the table is ALREADY clipped (needs
  // ~756px, has ~667px), so there is nothing spare to hand over — the clamp
  // must never narrow the table further than it is today.
  // Cap 520px: past that the description paragraph runs longer than a
  // comfortable reading measure.
  return (
    <aside
      role="complementary"
      aria-label="Criterion details"
      className="flex max-h-full w-[clamp(400px,26vw,520px)] min-w-0 max-w-full shrink flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm"
    >
      <DrawerBody {...shared} />
    </aside>
  )
}
